import React, { useState } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface OrganizationData {
  name: string;
  description: string;
  industry: string;
  size: string;
  location: string;
  energyGoals: string;
  teamSize: string;
  contactEmail: string;
  contactPhone: string;
}

interface CreateOrganizationStepTwoFormProps {
  organizationData: OrganizationData;
  updateOrganizationData: (data: Partial<OrganizationData>) => void;
  onNextStep: () => void;
}

// Custom Dropdown Component
const CustomDropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
}> = ({ value, onChange, placeholder, options }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-full border border-gray-200 bg-white px-6 py-4 text-left text-gray-900 transition-all focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>
          {value ? options.find((opt) => opt.value === value)?.label : placeholder}
        </span>
        <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute top-full left-0 z-20 mt-2 w-full rounded-2xl border border-gray-200 bg-white shadow-lg"
        >
          <div className="max-h-60 overflow-y-auto p-2">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full rounded-xl px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                  value === option.value ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

const CreateOrganizationStepTwoForm: React.FC<CreateOrganizationStepTwoFormProps> = ({
  organizationData,
  updateOrganizationData,
  onNextStep,
}) => {
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNextStep();
  };

  const teamSizeOptions = [
    { value: '1-5', label: '1-5 μέλη' },
    { value: '6-15', label: '6-15 μέλη' },
    { value: '16-30', label: '16-30 μέλη' },
    { value: '31-50', label: '31-50 μέλη' },
    { value: '50+', label: '50+ μέλη' },
  ];

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-cyan-50 to-red-50 p-6">
      {/* Decorative Elements */}
      {/* Avatar Circles - Top Left */}
      <div className="absolute top-8 left-8 flex items-center gap-2">
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-cyan-400 to-red-500 shadow-lg"></div>
        <div className="-ml-3 h-8 w-8 rounded-full bg-gradient-to-br from-yellow-400 to-cyan-500 shadow-lg"></div>
        <div className="-ml-2 h-6 w-6 rounded-full bg-gradient-to-br from-red-400 to-pink-500 shadow-lg"></div>
      </div>

      {/* Decorative Circles */}
      <div className="absolute top-20 right-16 h-20 w-20 rounded-full bg-gradient-to-br from-cyan-200/30 to-red-200/30 blur-sm"></div>
      <div className="absolute bottom-32 left-16 h-16 w-16 rounded-full bg-gradient-to-br from-yellow-200/40 to-cyan-200/40 blur-sm"></div>
      <div className="absolute right-32 bottom-16 h-12 w-12 rounded-full bg-gradient-to-br from-red-200/50 to-pink-200/50 blur-sm"></div>
      <div className="absolute top-1/3 right-8 h-8 w-8 rounded-full bg-gradient-to-br from-amber-200/60 to-yellow-200/60 blur-sm"></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Header */}
        <div className="mb-12 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-4 text-4xl font-bold text-gray-900"
          >
            Διαμόρφωση{' '}
            <span className="relative">
              οργανισμού
              <div className="absolute -bottom-1 left-0 h-3 w-full rounded-full bg-gradient-to-r from-cyan-200 via-red-200 to-pink-200 opacity-60"></div>
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-gray-600"
          >
            Ρυθμίσεις και στόχοι για τον
            <br />
            ενεργειακό σας οργανισμό
          </motion.p>
        </div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          {/* Location & Team Size */}
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              value={organizationData.location}
              onChange={(e) => updateOrganizationData({ location: e.target.value })}
              className="rounded-full border border-gray-200 bg-white px-6 py-4 text-gray-900 placeholder-gray-400 transition-all focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none"
              placeholder="Τοποθεσία"
              required
            />
            <CustomDropdown
              value={organizationData.teamSize}
              onChange={(value) => updateOrganizationData({ teamSize: value })}
              placeholder="Μέγεθος Ομάδας"
              options={teamSizeOptions}
            />
          </div>

          {/* Energy Goals */}
          <textarea
            value={organizationData.energyGoals}
            onChange={(e) => updateOrganizationData({ energyGoals: e.target.value })}
            rows={4}
            className="w-full resize-none rounded-2xl border border-gray-200 bg-white px-6 py-4 text-gray-900 placeholder-gray-400 transition-all focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none"
            placeholder="Στόχοι Ενέργειας"
            required
          />

          {/* Contact Email & Phone */}
          <div className="grid grid-cols-2 gap-4">
            <input
              type="email"
              value={organizationData.contactEmail}
              onChange={(e) => updateOrganizationData({ contactEmail: e.target.value })}
              className="rounded-full border border-gray-200 bg-white px-6 py-4 text-gray-900 placeholder-gray-400 transition-all focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none"
              placeholder="Email Επικοινωνίας"
              required
            />
            <input
              type="tel"
              value={organizationData.contactPhone}
              onChange={(e) => updateOrganizationData({ contactPhone: e.target.value })}
              className="rounded-full border border-gray-200 bg-white px-6 py-4 text-gray-900 placeholder-gray-400 transition-all focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none"
              placeholder="Τηλέφωνο (προαιρετικό)"
            />
          </div>

          {/* Next Step Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            type="submit"
            className="flex w-full items-center justify-center gap-3 rounded-full bg-black py-4 font-medium text-white transition-all hover:bg-gray-800"
          >
            <ArrowRight className="h-5 w-5" />
            Επόμενο Βήμα
          </motion.button>
        </motion.form>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="mt-8 text-center"
        >
          <span className="text-gray-500">Θέλετε να επιστρέψετε; </span>
          <button
            onClick={() => navigate('/organizations')}
            className="font-medium text-black underline hover:text-gray-700"
          >
            Επιστροφή
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default CreateOrganizationStepTwoForm;
